// Locale-aware форматирование чисел/дат/валют/длительности.
// Используем нативный Intl, без сторонних libs, чтобы не раздувать bundle.

type Locale = string;

const intlLocale = (locale: Locale): string => {
	// Маппинг ISO 639-1 → BCP-47, упрощённый: для большинства языков совпадает.
	// В будущем расширяется (например, 'zh' → 'zh-Hans', 'ar' → 'ar-SA').
	switch (locale) {
		case "ru":
			return "ru-RU";
		case "en":
			return "en-US";
		case "uk":
			return "uk-UA";
		default:
			return locale;
	}
};

// Форматтеры Intl кэшируются по (локаль + набор опций).
//
// Создание форматтера — одна из самых дорогих операций Intl: он разбирает
// локаль и собирает внутренние таблицы. Здесь же функции вызываются в теле
// рендера списков, то есть по разу НА КАЖДЫЙ элемент: библиотека рисует до 40
// карточек за проход, календарь — сетку на месяц. Сериализация опций в ключ
// на порядок дешевле конструктора, поэтому кэш выигрывает даже при разных
// наборах опций.
//
// Набор не ограничен по размеру намеренно: комбинаций локаль×опции в
// приложении единицы, они заданы кодом, а не пользовательским вводом.
const formatterCache = new Map<
	string,
	Intl.DateTimeFormat | Intl.NumberFormat | Intl.RelativeTimeFormat
>();

function cached<T extends Intl.DateTimeFormat | Intl.NumberFormat | Intl.RelativeTimeFormat>(
	kind: string,
	locale: string,
	options: object,
	create: () => T,
): T {
	const key = `${kind}|${locale}|${JSON.stringify(options)}`;
	const hit = formatterCache.get(key);
	if (hit) return hit as T;
	const made = create();
	formatterCache.set(key, made);
	return made;
}

export function formatDate(
	date: Date | string | number,
	locale: Locale = "ru",
	options: Intl.DateTimeFormatOptions = { dateStyle: "medium" },
): string {
	const d = typeof date === "string" || typeof date === "number" ? new Date(date) : date;
	return cached(
		"date",
		intlLocale(locale),
		options,
		() => new Intl.DateTimeFormat(intlLocale(locale), options),
	).format(d);
}

export function formatNumber(
	value: number,
	locale: Locale = "ru",
	options: Intl.NumberFormatOptions = {},
): string {
	return cached(
		"num",
		intlLocale(locale),
		options,
		() => new Intl.NumberFormat(intlLocale(locale), options),
	).format(value);
}

/**
 * Форматирует сумму в минорных единицах (копейки/центы) в строку валюты.
 * @example formatCurrency(1250, 'USD', 'en') -> "$12.50"
 */
export function formatCurrency(
	minor: number,
	currency: "RUB" | "USD" | "EUR" = "USD",
	locale: Locale = "en",
): string {
	const major = minor / 100;
	const options: Intl.NumberFormatOptions = {
		style: "currency",
		currency,
		// Для рубля и крупных сумм скрываем дробную часть, если она нулевая.
		maximumFractionDigits: Number.isInteger(major) ? 0 : 2,
	};
	return cached(
		"num",
		intlLocale(locale),
		options,
		() => new Intl.NumberFormat(intlLocale(locale), options),
	).format(major);
}

/**
 * Форматирует длительность в секундах в формат HH:MM:SS / MM:SS.
 */
export function formatDuration(totalSeconds: number): string {
	const s = Math.max(0, Math.floor(totalSeconds));
	const hours = Math.floor(s / 3600);
	const minutes = Math.floor((s % 3600) / 60);
	const seconds = s % 60;
	const pad = (n: number): string => n.toString().padStart(2, "0");
	if (hours > 0) {
		return `${hours}:${pad(minutes)}:${pad(seconds)}`;
	}
	return `${pad(minutes)}:${pad(seconds)}`;
}

/** Порог: ≥ 7 дней → абсолютная дата с годом (entity lists). */
export const ENTITY_RELATIVE_MAX_MS = 7 * 24 * 60 * 60 * 1000;

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

/** Единицы для списков сущностей: максимум day (без week/month/year). */
const ENTITY_RELATIVE_UNITS: Array<{
	unit: Intl.RelativeTimeFormatUnit;
	ms: number;
}> = [
	{ unit: "day", ms: DAY_MS },
	{ unit: "hour", ms: HOUR_MS },
	{ unit: "minute", ms: MINUTE_MS },
	{ unit: "second", ms: 1000 },
];

const RELATIVE_TIME_UNITS: Array<{
	unit: Intl.RelativeTimeFormatUnit;
	ms: number;
}> = [
	{ unit: "year", ms: 365 * DAY_MS },
	{ unit: "month", ms: 30 * DAY_MS },
	{ unit: "week", ms: 7 * DAY_MS },
	...ENTITY_RELATIVE_UNITS,
];

function toValidDate(date: Date | string | number): Date | null {
	const d = typeof date === "string" || typeof date === "number" ? new Date(date) : date;
	return Number.isNaN(d.getTime()) ? null : d;
}

function formatRelativeWithUnits(
	date: Date,
	locale: Locale,
	units: Array<{ unit: Intl.RelativeTimeFormatUnit; ms: number }>,
	nowMs: number = Date.now(),
): string {
	const diffMs = date.getTime() - nowMs;
	const absMs = Math.abs(diffMs);
	const rtfOptions: Intl.RelativeTimeFormatOptions = {
		numeric: "auto",
	};
	const rtf = cached(
		"rel",
		intlLocale(locale),
		rtfOptions,
		() => new Intl.RelativeTimeFormat(intlLocale(locale), rtfOptions),
	);
	for (const { unit, ms } of units) {
		if (absMs >= ms || unit === "second") {
			return rtf.format(Math.round(diffMs / ms), unit);
		}
	}
	return rtf.format(0, "second");
}

/**
 * Относительное время без ограничения по неделям/месяцам.
 * Для entity lists предпочтителен {@link formatEntityTimestamp}.
 */
export function formatRelativeTime(date: Date | string | number, locale: Locale = "ru"): string {
	const d = toValidDate(date);
	if (!d) return "";
	return formatRelativeWithUnits(d, locale, RELATIVE_TIME_UNITS);
}

/** Абсолютная дата с годом (день + короткий месяц + год). */
export function formatEntityAbsoluteDate(
	date: Date | string | number,
	locale: Locale = "ru",
): string {
	const d = toValidDate(date);
	if (!d) return "";
	const options: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };
	return cached(
		"date",
		intlLocale(locale),
		options,
		() => new Intl.DateTimeFormat(intlLocale(locale), options),
	).format(d);
}

/** Точная дата+время для tooltip. */
export function formatAbsoluteDateTime(
	date: Date | string | number,
	locale: Locale = "ru",
): string {
	const d = toValidDate(date);
	if (!d) return "";
	const options: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" };
	return cached(
		"date",
		intlLocale(locale),
		options,
		() => new Intl.DateTimeFormat(intlLocale(locale), options),
	).format(d);
}

/**
 * Единый штамп сущности: &lt; 7 дней → relative (до day), иначе абсолютная дата с годом.
 */
export function formatEntityTimestamp(
	date: Date | string | number,
	locale: Locale = "ru",
	nowMs: number = Date.now(),
): string {
	const d = toValidDate(date);
	if (!d) return "";
	const absMs = Math.abs(d.getTime() - nowMs);
	if (absMs < ENTITY_RELATIVE_MAX_MS) {
		return formatRelativeWithUnits(d, locale, ENTITY_RELATIVE_UNITS, nowMs);
	}
	return formatEntityAbsoluteDate(d, locale);
}
